import { getAuth } from 'firebase/auth';
import { logError } from './error-logger';

const normalizeBaseUrl = (value) => (value || '').trim().replace(/\/+$/, '');

const backendBaseUrl = normalizeBaseUrl(process.env.REACT_APP_BACKEND_API_URL);
const DEFAULT_CACHE_TTL_MS = 15000;

const responseCache = new Map();
const pendingRequests = new Map();

export const isBackendApiEnabled = () => Boolean(backendBaseUrl);

const buildUrl = (path, query) => {
    const url = new URL(`${backendBaseUrl}${path}`);
    if (query) {
        Object.entries(query).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                url.searchParams.set(key, String(value));
            }
        });
    }
    return url.toString();
};

const getAuthHeaders = async () => {
    const headers = {
        'Content-Type': 'application/json'
    };

    const auth = getAuth();
    const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;

    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    return headers;
};

const buildCacheKey = (url, method, body) => `${method}:${url}:${body ? JSON.stringify(body) : ''}`;
const invalidateGetCache = () => {
    responseCache.clear();
};

export const httpRequest = async (path, options = {}) => {
    if (!isBackendApiEnabled()) {
        throw new Error('Backend API is disabled. Missing REACT_APP_BACKEND_API_URL.');
    }

    const {
        method = 'GET',
        query,
        body,
        cache = method === 'GET',
        cacheTtlMs = DEFAULT_CACHE_TTL_MS
    } = options;

    const headers = await getAuthHeaders();
    const requestUrl = buildUrl(path, query);
    const cacheKey = buildCacheKey(requestUrl, method, body);
    const isGetRequest = method === 'GET';
    const shouldUseCache = isGetRequest && cache;

    if (shouldUseCache) {
        const cached = responseCache.get(cacheKey);
        if (cached && cached.expiresAt > Date.now()) {
            return cached.value;
        }
        if (pendingRequests.has(cacheKey)) {
            return pendingRequests.get(cacheKey);
        }
    }

    const executeRequest = async () => {
        try {
            const response = await fetch(requestUrl, {
                method,
                headers,
                body: body !== undefined ? JSON.stringify(body) : undefined
            });

            if (!response.ok) {
                const responseText = await response.text();
                throw new Error(`HTTP ${response.status}: ${responseText || response.statusText}`);
            }

            if (response.status === 204) {
                if (!isGetRequest) {
                    invalidateGetCache();
                }
                return null;
            }

            const result = await response.json();
            if (shouldUseCache) {
                responseCache.set(cacheKey, {
                    value: result,
                    expiresAt: Date.now() + Math.max(0, cacheTtlMs)
                });
            } else if (!isGetRequest) {
                invalidateGetCache();
            }
            return result;
        } catch (error) {
            logError('HTTP request failed', error, {
                path,
                method
            });
            throw error;
        } finally {
            if (shouldUseCache) {
                pendingRequests.delete(cacheKey);
            }
        }
    };

    if (shouldUseCache) {
        const requestPromise = executeRequest();
        pendingRequests.set(cacheKey, requestPromise);
        return requestPromise;
    }

    return executeRequest();
};
